import { useMemo, useState } from 'react';
import { isEmpty } from 'ramda';

import { REMINDER_MAX_OCCURRENCES_UNLIMITED } from 'features/Work/constants';

import { SelectableFrequency, SidebarProps } from '../types';
import {
  getMaxOccurrencesToSave,
  mapReminderFrequencyWithSelectFrequency,
} from '../utils';

type ValueType = string | string[] | number | boolean | null;

interface FormFieldsDataType {
  clientContactIds: string[];
  reminderFrequency: SelectableFrequency | null;
  isReminderEnabled: boolean;
  isUserCcedOnEmails: boolean;
  reminderMaxOccurrences: number;
}

const useEmailReminderFormState = ({ client, invoice }: SidebarProps) => {
  const { clientContactIds, isUserCcedOnEmails, settings } = invoice;
  const { contacts, type } = client;

  const defaultClientContactIds =
    type === 'PRIVATE_INDIVIDUAL' && contacts?.[0]
      ? [contacts[0].clientContactId]
      : clientContactIds;

  const initialFrequency = mapReminderFrequencyWithSelectFrequency(
    settings.reminderFrequency,
    settings.reminderMaxOccurrences,
  );

  const initialState: FormFieldsDataType = {
    clientContactIds: defaultClientContactIds,
    isReminderEnabled: settings.isReminderEnabled,
    isUserCcedOnEmails,
    reminderFrequency: initialFrequency,
    reminderMaxOccurrences:
      settings.reminderMaxOccurrences === REMINDER_MAX_OCCURRENCES_UNLIMITED ||
      settings.reminderMaxOccurrences === null
        ? 4 // Set default 4 weeks occurrences in case the previous frequency was set to EVERY_WEEK_UNTIL_PAYMENT
        : settings.reminderMaxOccurrences,
  };

  const [state, setState] = useState(initialState);

  const formDataHasChanged = useMemo(
    () =>
      settings.reminderMaxOccurrences !==
        getMaxOccurrencesToSave(
          state.reminderFrequency,
          state.reminderMaxOccurrences,
        ) ||
      settings.isReminderEnabled !== state.isReminderEnabled ||
      isUserCcedOnEmails !== state.isUserCcedOnEmails ||
      initialFrequency !== state.reminderFrequency ||
      JSON.stringify(clientContactIds) !==
        JSON.stringify(state.clientContactIds),
    [
      clientContactIds,
      initialFrequency,
      isUserCcedOnEmails,
      settings.isReminderEnabled,
      settings.reminderMaxOccurrences,
      state.clientContactIds,
      state.isReminderEnabled,
      state.isUserCcedOnEmails,
      state.reminderFrequency,
      state.reminderMaxOccurrences,
    ],
  );
  const isGivenWeeksFrequency = state.reminderFrequency === 'FOR_GIVEN_WEEKS';

  const isFormInvalid =
    isEmpty(state.clientContactIds) ||
    (isGivenWeeksFrequency && !state.reminderMaxOccurrences);

  return {
    formDataHasChanged,
    isFormInvalid,
    isGivenWeeksFrequency,
    onChange: (field: keyof FormFieldsDataType) => (value: ValueType) =>
      setState({ ...state, [field]: value }),
    state,
  };
};

export default useEmailReminderFormState;
